import '../config/loadEnv.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


import IORedis from 'ioredis'
import type { Redis as IORedisType } from 'ioredis'
import { Redis as UpstashRedis } from '@upstash/redis'
import { createLogger } from '../../common/utils/logger.js'
import { redisConfig, redisDriver, supportsRateLimitRedis as configSupportsRateLimitRedis } from '../config/redisConfig.js'

const logger = createLogger('redis')

type RedisValue = string | Buffer

export interface RedisClient {
  get(key: string): Promise<string | null>
  getBuffer(key: string): Promise<Buffer | null>
  set(key: string, value: RedisValue, options?: { ex?: number }): Promise<string>
  setex(key: string, ttl: number, value: RedisValue): Promise<string>
  del(...keys: string[]): Promise<number>
  hgetall(key: string): Promise<Record<string, string>>
  hset(key: string, field: string, value: string): Promise<number>
  expire(key: string, seconds: number): Promise<number>
  incr(key: string): Promise<number>
  lpush(key: string, value: string): Promise<number>
  ltrim(key: string, start: number, stop: number): Promise<number>
  keys(pattern: string): Promise<string[]>
  ttl(key: string): Promise<number>
  memoryUsage?(key: string): Promise<number | null>
  ping(): Promise<string>
  disconnect(): Promise<void>
  // Optional: used by fastify-rate-limit when an ioredis client is provided
  defineCommand?(name: string, options: unknown): unknown
}

class UpstashRedisAdapter implements RedisClient {
  constructor(private readonly client: UpstashRedis) {
    logger.info('Using Upstash Redis client')
  }

  async get(key: string): Promise<string | null> {
    return (await this.client.get<string | null>(key)) ?? null
  }

  async getBuffer(key: string): Promise<Buffer | null> {
    const value = await this.client.get(key)
    if (!value) return null
    
    // Handle different value types from Upstash
    if (isString(value)) {
      // Try to parse as base64, fallback to UTF-8
      try {
        return Buffer.from(value, 'base64')
      } catch {
        return Buffer.from(value, 'utf-8')
      }
    }
    
    // If it's already an object or other type, convert to JSON string then buffer
    if (typeof value === 'object') {
      return Buffer.from(JSON.stringify(value), 'utf-8')
    }
    
    // Fallback for other primitive types
    return Buffer.from(JSON.stringify(value), 'utf-8')
  }

  async set(key: string, value: RedisValue, options?: { ex?: number }): Promise<string> {
    const payload = Buffer.isBuffer(value) ? value.toString('base64') : value
    const result = options?.ex
      ? await this.client.set(key, payload, { ex: options.ex })
      : await this.client.set(key, payload)
    return (result as string) ?? 'OK'
  }

  async setex(key: string, ttl: number, value: RedisValue): Promise<string> {
    const payload = Buffer.isBuffer(value) ? value.toString('base64') : value
    const result = await this.client.set(key, payload, { ex: ttl })
    return (result as string) ?? 'OK'
  }

  async lpush(key: string, value: string): Promise<number> {
    const res = await this.client.lpush(key, value)
    return typeof res === 'number' ? res : Number(res)
  }

  async ltrim(key: string, start: number, stop: number): Promise<number> {
    // Upstash may return 'OK' for ltrim; return 1 on success
    await this.client.ltrim(key, start, stop)
    return 1
  }

  async del(...keys: string[]): Promise<number> {
    if (keys.length === 0) return 0
    return await this.client.del(...keys)
  }

  async hgetall(key: string): Promise<Record<string, string>> {
    const result = (await this.client.hgetall<Record<string, unknown>>(key)) ?? {}
    const normalized: Record<string, string> = {}

    for (const [field, value] of Object.entries(result)) {
      if (isString(value)) {
        normalized[field] = value
      } else if (value == null) {
        normalized[field] = ''
      } else {
        normalized[field] = JSON.stringify(value)
      }
    }

    return normalized
  }

  async hset(key: string, field: string, value: string): Promise<number> {
    return await this.client.hset(key, { [field]: value })
  }

  async expire(key: string, seconds: number): Promise<number> {
    return await this.client.expire(key, seconds)
  }

  async incr(key: string): Promise<number> {
    return await this.client.incr(key)
  }

  async keys(pattern: string): Promise<string[]> {
    return await this.client.keys(pattern)
  }

  async ttl(key: string): Promise<number> {
    const value = await this.client.ttl(key)
    return typeof value === 'number' ? value : Number(value)
  }

  async memoryUsage(key: string): Promise<number | null> {
    // Upstash REST API does not support MEMORY USAGE command
    // Fallback: estimate size by fetching the value
    try {
      const value = await this.client.get(key)
      if (!value) return null
      
      if (isString(value)) {
        return Buffer.byteLength(value, 'utf-8')
      }
      if (typeof value === 'object') {
        return Buffer.byteLength(JSON.stringify(value), 'utf-8')
      }
      // Fallback for other primitive types
      return Buffer.byteLength(JSON.stringify(value), 'utf-8')
    } catch (error) {
      logger.warn('[UpstashRedisAdapter] memoryUsage estimation failed', { error })
      return null
    }
  }

  async ping(): Promise<string> {
    const result = await this.client.ping()
    return isString(result)? result : 'PONG'
  }

  async disconnect(): Promise<void> {
    // Upstash client is stateless
  }
}

class IoRedisAdapter implements RedisClient {
  private connectHandler?: () => void
  private readyHandler?: () => void
  private errorHandler?: (err: Error) => void
  private reconnectingHandler?: (delay: number) => void
  private closeHandler?: () => void

  constructor(private readonly client: IORedisType) {
    this.connectHandler = () => {
      logger.info('✅ Redis connected')
    }
    this.readyHandler = () => {
      logger.info('✅ Redis ready to accept commands')
    }
    this.errorHandler = (err) => {
      logger.error('❌ Redis connection error', { message: err.message })
    }
    this.reconnectingHandler = (delay: number) => {
      logger.info(`🔄 Reconnecting to Redis in ${delay}ms...`)
    }
    this.closeHandler = () => {
      logger.warn('⚠️ Redis connection closed')
    }

    this.client.on('connect', this.connectHandler)
    this.client.on('ready', this.readyHandler)
    this.client.on('error', this.errorHandler)
    this.client.on('reconnecting', this.reconnectingHandler)
    this.client.on('close', this.closeHandler)
  }

  async get(key: string): Promise<string | null> {
    return await this.client.get(key)
  }

  async getBuffer(key: string): Promise<Buffer | null> {
    const value = await this.client.getBuffer(key)
    return value ?? null
  }

  async set(key: string, value: RedisValue, options?: { ex?: number }): Promise<string> {
    if (options?.ex) {
      return await this.client.set(key, value, 'EX', options.ex)
    }
    return await this.client.set(key, value)
  }

  async setex(key: string, ttl: number, value: RedisValue): Promise<string> {
    return await this.client.setex(key, ttl, value)
  }

  async lpush(key: string, value: string): Promise<number> {
    return await this.client.lpush(key, value)
  }

  async ltrim(key: string, start: number, stop: number): Promise<number> {
    await this.client.ltrim(key, start, stop)
    return 1
  }

  async del(...keys: string[]): Promise<number> {
    if (keys.length === 0) return 0
    return await this.client.del(...keys)
  }

  async hgetall(key: string): Promise<Record<string, string>> {
    return await this.client.hgetall(key)
  }

  async hset(key: string, field: string, value: string): Promise<number> {
    return await this.client.hset(key, field, value)
  }

  async expire(key: string, seconds: number): Promise<number> {
    return await this.client.expire(key, seconds)
  }

  async incr(key: string): Promise<number> {
    return await this.client.incr(key)
  }

  async keys(pattern: string): Promise<string[]> {
    return await this.client.keys(pattern)
  }

  async ttl(key: string): Promise<number> {
    return await this.client.ttl(key)
  }

  async memoryUsage(key: string): Promise<number | null> {
    try {
      // @ts-expect-error - memoryUsage exists on Redis but not properly typed
      const result = await this.client.memoryUsage(key)
      return typeof result === 'number' ? result : Number(result ?? 0)
    } catch (error) {
      logger.warn('[IoRedisAdapter] memoryUsage failed', { error })
      return null
    }
  }

  async ping(): Promise<string> {
    return await this.client.ping()
  }

  defineCommand(name: string, options: unknown) {
    // ioredis exposes defineCommand, but it's not typed on the interface
    const anyClient = this.client as unknown as { defineCommand?: (cmd: string, opts: unknown) => unknown }
    return anyClient.defineCommand?.(name, options)
  }

  async disconnect(): Promise<void> {
    // Clean up event listeners to prevent memory leaks
    if (this.connectHandler) this.client.off('connect', this.connectHandler)
    if (this.readyHandler) this.client.off('ready', this.readyHandler)
    if (this.errorHandler) this.client.off('error', this.errorHandler)
    if (this.reconnectingHandler) this.client.off('reconnecting', this.reconnectingHandler)
    if (this.closeHandler) this.client.off('close', this.closeHandler)
    
    await this.client.quit()
  }
}

const createPrimaryRedisClient = (): RedisClient => {
  if (redisDriver === 'upstash') {
    if (!redisConfig.upstashUrl || !redisConfig.upstashToken || !redisConfig.upstashHost) {
      logger.warn('Upstash driver selected but UPSTASH_REDIS_REST_URL/TOKEN are missing or invalid, falling back to local Redis')
    } else {
      const upstashClient = new UpstashRedis({ url: redisConfig.upstashUrl, token: redisConfig.upstashToken })
      return new UpstashRedisAdapter(upstashClient)
    }
  }

  const redisUrl = process.env.REDIS_URL
  const fallbackOptions = {
    host: redisConfig.redisHost,
    port: redisConfig.redisPort,
    password: redisConfig.redisPassword,
    tls: redisConfig.redisTls ? {} : undefined,
    retryStrategy: (times: number) => {
      const delay = Math.min(times * 50, 2000)
      logger.debug(`🔄 Redis retry attempt ${times}, delay: ${delay}ms`)
      return delay
    },
    maxRetriesPerRequest: 3,
    lazyConnect: true
  }

  if (redisConfig.driver === 'upstash') {
    logger.info('Using local Redis because REDIS_DRIVER=upstash is not fully configured')
  } else if (redisConfig.driver === 'ioredis' && redisConfig.hasUpstashCreds) {
    logger.info('Using local Redis (REDIS_DRIVER forces local even though UPSTASH_* are set)')
  } else {
    logger.info('Using local Redis client (ioredis)')
  }

  const ioRedisClient = redisUrl
    ? new IORedis(redisUrl, fallbackOptions)
    : new IORedis(fallbackOptions)

  return new IoRedisAdapter(ioRedisClient)
}

const createNoopRedisClient = (): RedisClient => ({
  async get() { return null },
  async getBuffer() { return null },
  async set() { return 'OK' },
  async setex() { return 'OK' },
  async del() { return 0 },
  async hgetall() { return {} },
  async hset() { return 0 },
  async expire() { return 0 },
  async incr() { return 0 },
  async lpush() { return 0 },
  async ltrim() { return 0 },
  async keys() { return [] },
  async ttl() { return -1 },
  async memoryUsage() { return null },
  async ping() { return 'PONG' },
  async disconnect() { /* noop */ },
  defineCommand() {
    // fastify-rate-limit expects this on ioredis; noop keeps dev servers alive when Redis is down
    return undefined
  }
})

let redisDelegate: RedisClient = createPrimaryRedisClient()
const noopRedis = createNoopRedisClient()
let redisDegraded = false

const switchToNoop = (reason?: unknown) => {
  if (!redisDegraded) {
    logger.warn('⚠️ Redis unavailable, switching to noop cache', {
      reason: reason instanceof Error ? reason.message : reason
    })
    redisDegraded = true
  }
  redisDelegate = noopRedis
}

const safeCall = async <T>(operation: string, fallback: T, executor: () => Promise<T>): Promise<T> => {
  try {
    return await executor()
  } catch (error) {
    switchToNoop(error)
    logger.debug('[redis] Fallback used', { operation })
    return fallback
  }
}

const safeRedis: RedisClient = {
  get: key => safeCall('get', null, () => redisDelegate.get(key)),
  getBuffer: key => safeCall('getBuffer', null, () => redisDelegate.getBuffer(key)),
  set: (key, value, options) => safeCall('set', 'OK', () => redisDelegate.set(key, value, options)),
  setex: (key, ttl, value) => safeCall('setex', 'OK', () => redisDelegate.setex(key, ttl, value)),
  del: (...keys) => safeCall('del', 0, () => redisDelegate.del(...keys)),
  hgetall: key => safeCall('hgetall', {}, () => redisDelegate.hgetall(key)),
  hset: (key, field, value) => safeCall('hset', 0, () => redisDelegate.hset(key, field, value)),
  expire: (key, seconds) => safeCall('expire', 0, () => redisDelegate.expire(key, seconds)),
  incr: key => safeCall('incr', 0, () => redisDelegate.incr(key)),
  lpush: (key, value) => safeCall('lpush', 0, () => redisDelegate.lpush(key, value)),
  ltrim: (key, start, stop) => safeCall('ltrim', 0, () => redisDelegate.ltrim(key, start, stop)),
  keys: pattern => safeCall('keys', [], () => redisDelegate.keys(pattern)),
  ttl: key => safeCall('ttl', -1, () => redisDelegate.ttl(key)),
  memoryUsage: key => safeCall('memoryUsage', null, async () => redisDelegate.memoryUsage ? await redisDelegate.memoryUsage(key) : null),
  ping: () => safeCall('ping', 'PONG', () => redisDelegate.ping()),
  disconnect: () => safeCall('disconnect', undefined, () => redisDelegate.disconnect()),
  defineCommand: (name: string, options: unknown) => {
    const delegate = redisDelegate as RedisClient & { defineCommand?: (name: string, options: unknown) => unknown }
    return delegate.defineCommand?.(name, options)
  }
}

// Rate-limit plugin requires a Redis client that supports defineCommand (ioredis/node-redis).
// Upstash REST client does not, so we expose a flag to avoid wiring it.
export const supportsRateLimitRedis = configSupportsRateLimitRedis
export { redisDriver }

export async function initializeRedis(): Promise<RedisClient> {
  try {
    await redisDelegate.ping()
    logger.info('✅ Redis initialized successfully')
  } catch (err) {
    logger.error('⚠️ Redis not available, running without cache', { message: (err as Error).message })
    switchToNoop(err)
  }

  return safeRedis
}

// Export adapter instance
export const redis: RedisClient = safeRedis
export const redisClient: RedisClient = safeRedis

/**
 * Safely parse JSON from Redis cache
 * @param cached - The cached string value from Redis
 * @param cacheKey - The cache key (for logging)
 * @returns Parsed object or null if parsing fails
 */
export function safeJsonParse<T>(cached: unknown, cacheKey: string): T | null {
  if (cached == null) return null

  // If the value is already an object (parsed by some clients), return it directly
  if (typeof cached === 'object') {
    return cached as T
  }

  // If it's a Buffer (unlikely from get), convert to string
  let raw: string
  if (Buffer.isBuffer(cached)) {
    raw = cached.toString('utf8')
  } else if (isString(cached)) {
    raw = cached
  } else {
    // Fallback: stringify whatever it is and attempt to parse
    try {
      raw = JSON.stringify(cached)
    } catch (e) {
      logger.error('Unable to stringify cached value', { key: cacheKey, error: e })
      return null
    }
  }

  try {
    return JSON.parse(raw) as T
  } catch (error) {
    const preview = raw.length > 0 ? raw.slice(0, 100) + (raw.length > 100 ? '...' : '') : '<empty>'
    logger.error('Failed to parse JSON from Redis', { key: cacheKey, error, preview })
    // Delete the corrupted cache key (best-effort)
    redisDelegate.del(cacheKey).catch(err => {
      logger.error('Failed to delete corrupted Redis key', { key: cacheKey, error: err })
    })
    return null
  }
}
